import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { UiToggleComponent } from '@/ui/toggle/ui-toggle';
import { observeControlValue$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { ApplyPureFunctionPipe } from '@/utils/angular/pipes/apply-pure-function.pipe';
import { doNotSort } from '@/utils/toolkit/array/sort.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    type OnChanges,
    type OnInit,
    Output,
    type SimpleChanges
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, type Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';
import { SortOrder } from '../../../../data-viz/data-source.interface';
import { AdvizeoInputNumberDirective } from '../../../../directives/advizeo-input/advizeo-input-number.directive';
import { AdvizeoInputDirective } from '../../../../directives/advizeo-input/advizeo-input.directive';
import {
    type BusinessIntelligenceDimension,
    type BusinessIntelligenceMeasure,
    type BusinessIntelligenceSortCriterion,
    BusinessIntelligenceTemporalDimensionStep
} from '../../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligenceHistogramRepresentationConfig } from '../../../../interfaces/BusinessIntelligenceRepresentation.interface';
import type { BusinessIntelligenceDimensionConfiguration } from '../../../../interfaces/TailoredWidget.interface';
import { BusinessIntelligenceProvider } from '../../../../providers/business-intelligence/business-intelligence.provider';
import { BusinessIntelligenceRepresentationConfigEditorComponent } from '../business-intelligence-representation-config-editor.class';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-histogram-representation-config-editor.scss'],
    selector: 'business-intelligence-histogram-representation-config-editor',
    templateUrl: 'business-intelligence-histogram-representation-config-editor.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        UiFormFieldComponent,
        UiSelectComponent,
        AdvizeoInputDirective,
        AdvizeoInputNumberDirective,
        UiToggleComponent,
        AsyncPipe,
        TranslatePipe,
        ApplyPureFunctionPipe
    ]
})
export class BusinessIntelligenceHistogramRepresentationConfigEditorComponent
    extends BusinessIntelligenceRepresentationConfigEditorComponent<BusinessIntelligenceHistogramRepresentationConfig>
    implements OnInit, OnChanges
{
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _fb = inject(UntypedFormBuilder);
    private _translateService = inject(TranslateService);
    private _observableInputsManager = new ObservableInputsManager<BusinessIntelligenceHistogramRepresentationConfigEditorComponent>(this);

    @Input() config!: BusinessIntelligenceHistogramRepresentationConfig;
    config$ = this._observableInputsManager.observe('config');

    @Input() availableMeasures: BusinessIntelligenceMeasure[] = [];
    availableMeasures$ = this._observableInputsManager.observe('availableMeasures');

    @Input() availableDimensions: BusinessIntelligenceDimension[] = [];
    availableDimensions$ = this._observableInputsManager.observe('availableDimensions');

    @Input() disabled!: boolean;

    @Output() configChanged: EventEmitter<BusinessIntelligenceHistogramRepresentationConfig> = new EventEmitter();

    formGroup!: UntypedFormGroup;
    segmentationDimensions$!: Observable<BusinessIntelligenceDimension[]>;
    xAxisDimensionInfo$!: Observable<{ isTemporalDimension: boolean; isContinuousDimension: boolean }>;
    isMeasureSegmentable$!: Observable<boolean>;
    stepList: { value: BusinessIntelligenceTemporalDimensionStep; label: string }[] = [];

    currentStep$!: Observable<BusinessIntelligenceTemporalDimensionStep>;
    months: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    doNotSort = doNotSort;

    ngOnInit() {
        this.stepList = Object.values(BusinessIntelligenceTemporalDimensionStep).map(step => {
            return {
                value: step,
                label: this._translateService.instant('STEPS.' + step.toUpperCase())
            };
        });

        this.segmentationDimensions$ = this.availableDimensions$.pipe(
            filter(isNotNullish),
            map(dimensions => dimensions.filter(dimension => !this._businessIntelligenceProvider.isContinuousDimension(dimension)))
        );

        this.formGroup = this._fb.group({
            measureCode: [null, Validators.required],
            xAxisDimensionCode: [null, Validators.required],
            xAxisStepCode: [null],
            startAt: [null],
            segmentationDimensionCode: [null],
            isSortReversed: [false],
            limit: [null],
            shouldZeroBeDisplayed: [false]
        });

        this.config$.pipe(untilDestroyed(this)).subscribe(config => {
            if (!config) {
                this.formGroup.setValue(
                    {
                        measureCode: null,
                        xAxisDimensionCode: null,
                        xAxisStepCode: null,
                        segmentationDimensionCode: null,
                        isSortReversed: false,
                        limit: null,
                        shouldZeroBeDisplayed: false
                    },
                    { emitEvent: false }
                );
            } else {
                this.formGroup.setValue(
                    {
                        measureCode: config.measure ? config.measure.measureCode : null,
                        xAxisDimensionCode: config.xAxis ? config.xAxis.dimensionCode : null,
                        xAxisStepCode:
                            config.xAxis && config.xAxis.groupingParams && config.xAxis.groupingParams.granularity
                                ? config.xAxis.groupingParams.granularity
                                : null,
                        startAt: config.xAxis?.groupingParams?.startAt ?? null,
                        segmentationDimensionCode: config.segmentation ? config.segmentation.dimensionCode : null,
                        isSortReversed: this.isSortReversed(config),
                        limit: isNotNullish(config.limit) ? config.limit : null,
                        shouldZeroBeDisplayed: config.shouldZeroBeDisplayed ?? false // default is for retro-compat
                    },
                    { emitEvent: false }
                );
            }
        });

        this.xAxisDimensionInfo$ = combineLatest([
            this.availableDimensions$.pipe(filter(isNotNullish)),
            observeControlValue$(this.formGroup.controls.xAxisDimensionCode)
        ]).pipe(
            map(([dimensionList, dimensionCode]) => {
                const dimensionFound = dimensionList.find(
                    dimension => dimension.dimensionCode === dimensionCode
                ) as BusinessIntelligenceDimension;
                return {
                    isTemporalDimension: this._businessIntelligenceProvider.isTemporalDimension(dimensionFound),
                    isContinuousDimension: this._businessIntelligenceProvider.isContinuousDimension(dimensionFound)
                };
            })
        );

        this.isMeasureSegmentable$ = combineLatest([
            this.availableMeasures$.pipe(filter(isNotNullish)),
            observeControlValue$(this.formGroup.controls.measureCode)
        ]).pipe(
            map(([availableMeasures, measureCode]) => {
                const measure = availableMeasures.find(m => m.measureCode === measureCode);
                return measure ? measure.isSegmentable : true;
            })
        );

        this.currentStep$ = observeControlValue$(this.formGroup.controls.xAxisStepCode);

        this.formGroup.valueChanges
            .pipe(
                withLatestFrom(
                    this.availableDimensions$.pipe(distinctUntilChanged()),
                    this.availableMeasures$.pipe(distinctUntilChanged())
                ),
                untilDestroyed(this)
            )
            .subscribe(([formValue, availableDimensions, availableMeasures]) => {
                const measure: BusinessIntelligenceMeasure | undefined = availableMeasures.find(
                    m => m.measureCode === formValue.measureCode
                );

                const segmentationDimension: BusinessIntelligenceDimensionConfiguration | null =
                    formValue.segmentationDimensionCode && measure && measure.isSegmentable
                        ? {
                              dimensionCode: formValue.segmentationDimensionCode
                          }
                        : null;

                const xAxisDimension = availableDimensions.find(d => d.dimensionCode === formValue.xAxisDimensionCode);

                const xAxisDimensionConfiguration: BusinessIntelligenceDimensionConfiguration | null = formValue.xAxisDimensionCode
                    ? {
                          dimensionCode: formValue.xAxisDimensionCode,
                          groupingParams:
                              this._businessIntelligenceProvider.isTemporalDimension(xAxisDimension) && formValue.xAxisStepCode
                                  ? formValue.startAt && this.isStartAtAvailable(formValue.xAxisStepCode)
                                      ? {
                                            granularity: formValue.xAxisStepCode,
                                            startAt: formValue.startAt
                                        }
                                      : {
                                            granularity: formValue.xAxisStepCode
                                        }
                                  : null
                      }
                    : null;
                const sortCriteria: BusinessIntelligenceSortCriterion[] = this.getSortCriteria(formValue, availableDimensions);
                this.configChanged.emit({
                    xAxis: xAxisDimensionConfiguration as BusinessIntelligenceDimensionConfiguration,
                    segmentation: segmentationDimension,
                    click: null,
                    measure: {
                        measureCode: formValue.measureCode
                    },
                    sortCriteria,
                    limit: !segmentationDimension ? formValue.limit : null,
                    shouldZeroBeDisplayed: formValue.shouldZeroBeDisplayed
                });
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }

    private getSortCriteria(formValue: any, availableDimensions: BusinessIntelligenceDimension[]): BusinessIntelligenceSortCriterion[] {
        if (formValue.segmentationDimensionCode) {
            return [];
        }
        if (formValue.xAxisDimensionCode) {
            const xAxisDimension = availableDimensions.find(dimension => dimension.dimensionCode === formValue.xAxisDimensionCode);
            if (this._businessIntelligenceProvider.isContinuousDimension(xAxisDimension)) {
                return [
                    {
                        memberCode: formValue.xAxisDimensionCode,
                        sortDirection: formValue.isSortReversed ? SortOrder.DESC : SortOrder.ASC
                    } as BusinessIntelligenceSortCriterion
                ];
            }
        }
        if (formValue.measureCode) {
            return [
                {
                    memberCode: formValue.measureCode,
                    sortDirection: formValue.isSortReversed ? SortOrder.ASC : SortOrder.DESC
                } as BusinessIntelligenceSortCriterion
            ];
        }
        return [];
    }

    private isSortReversed(config: BusinessIntelligenceHistogramRepresentationConfig): boolean {
        if (!config.sortCriteria || !config.sortCriteria.length) {
            return false;
        }
        const significantSortCriterion = config.sortCriteria[0];
        if (config.xAxis && significantSortCriterion.memberCode === config.xAxis.dimensionCode) {
            return significantSortCriterion.sortDirection === SortOrder.DESC;
        }
        if (config.measure && significantSortCriterion.memberCode === config.measure.measureCode) {
            return significantSortCriterion.sortDirection === SortOrder.ASC;
        }
        return false;
    }

    isStartAtAvailable(step: BusinessIntelligenceTemporalDimensionStep | null) {
        return step === 'year';
    }
}
